import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { Student, Lang } from '../types';
import { STORAGE_KEYS, getJSON, setJSON, removeItem, getString, setString } from '../utils/storage';
import { isRTL } from '../utils/i18n';

/** Sync <html lang> and <html dir> with the active language. */
function applyHtmlLang(l: Lang) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = l;
    document.documentElement.dir = isRTL(l) ? 'rtl' : 'ltr';
  }
}

/** Decode JWT payload to check expiry. Returns null if not a valid JWT. */
function getTokenExpiry(token: string): number | null {
  try {
    if (token.startsWith('guest-')) return null; // guest tokens don't expire
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const expiry = getTokenExpiry(token);
  if (expiry === null) return false; // can't determine — assume valid
  return Date.now() > expiry;
}

interface AuthContextType {
  student: Student | null;
  token: string | null;
  lang: Lang;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (student: Student, token: string) => void;
  logout: () => void;
  setLang: (lang: Lang) => void;
  updateStudent: (updates: Partial<Student>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [student, setStudent] = useState<Student | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [lang, setLangState] = useState<Lang>(() => {
    const initial = (getString(STORAGE_KEYS.LANG) as Lang) || 'he';
    applyHtmlLang(initial);
    // Kick off the translation pack for non-he users immediately so the
    // first paint has the right strings. Hebrew is bundled inline.
    if (initial !== 'he') {
      // Eagerly load + trigger a re-render once the dict arrives.
      void import('../utils/i18n').then(m => m.loadLanguage(initial));
    }
    return initial;
  });
  const [isLoading, setIsLoading] = useState(true);

  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set auto-logout timer for token expiry
  const scheduleAutoLogout = useCallback((tkn: string, logoutFn: () => void) => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    const expiry = getTokenExpiry(tkn);
    if (!expiry) return;
    const ms = expiry - Date.now() - 60000; // 1 min before expiry
    if (ms > 0) {
      expiryTimerRef.current = setTimeout(logoutFn, ms);
    }
  }, []);

  // Restore session on mount
  useEffect(() => {
    const savedToken = getString(STORAGE_KEYS.AUTH_TOKEN) || null;
    const savedStudent = getJSON<Student | null>(STORAGE_KEYS.STUDENT, null);
    if (savedToken && savedStudent) {
      // Guest mode was removed — a guest session (no real backend account)
      // has nearly all features disabled, which users mistook for "the app
      // is broken". Evict any leftover guest session so they re-auth with
      // Google or a real registration.
      const isGuest = savedStudent.id === 'guest' || savedToken.startsWith('guest-');
      if (isGuest) {
        removeItem(STORAGE_KEYS.AUTH_TOKEN);
        removeItem(STORAGE_KEYS.STUDENT);
        setIsLoading(false);
        return;
      }
      if (isTokenExpired(savedToken)) {
        // Token expired — clear session
        removeItem(STORAGE_KEYS.AUTH_TOKEN);
        removeItem(STORAGE_KEYS.STUDENT);
      } else {
        setToken(savedToken);
        setStudent(savedStudent);
        // Schedule auto-logout for restored session (use a deferred logout ref)
        scheduleAutoLogout(savedToken, () => {
          removeItem(STORAGE_KEYS.AUTH_TOKEN);
          removeItem(STORAGE_KEYS.STUDENT);
          setStudent(null);
          setToken(null);
        });
      }
    }
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    setStudent(null);
    setToken(null);
    removeItem(STORAGE_KEYS.AUTH_TOKEN);
    removeItem(STORAGE_KEYS.STUDENT);
  }, []);

  const login = useCallback((student: Student, token: string) => {
    setStudent(student);
    setToken(token);
    setJSON(STORAGE_KEYS.STUDENT, student);
    setString(STORAGE_KEYS.AUTH_TOKEN, token);
    scheduleAutoLogout(token, logout);
  }, [scheduleAutoLogout, logout]);

  const setLang = useCallback((l: Lang) => {
    // Load the translation pack BEFORE switching, so the first render
    // after switch has the strings. He is sync (no-op); others come via
    // a lazy chunk.
    void import('../utils/i18n').then(m => m.loadLanguage(l)).then(() => {
      setLangState(l);
      setString(STORAGE_KEYS.LANG, l);
      applyHtmlLang(l);
    });
  }, []);

  const updateStudent = useCallback((updates: Partial<Student>) => {
    setStudent(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      setJSON(STORAGE_KEYS.STUDENT, updated);
      // Best-effort backend sync for track changes — without this the
      // parent dashboard still reads the original track_id ('3u' default)
      // from the backend Student record. Local UI updates immediately;
      // the backend catches up async.
      if (updates.track && updated.id && token) {
        void import('../utils/api').then(m =>
          m.updateStudentPreferences(updated.id, { track_id: updates.track }, token).catch(() => { /* silent */ })
        );
      }
      return updated;
    });
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        student,
        token,
        lang,
        isLoggedIn: !!student && !!token,
        isLoading,
        login,
        logout,
        setLang,
        updateStudent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
